import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { WorkloadContextData, WorkloadData, WorkloadProviderProps } from './types';
import { TWO_WEEKLY_LIMIT, MONTHLY_LIMIT, initialState } from './constants';
import {
  countSchedulesByDay,
  generateDays,
  generateMonthlyColumns,
  weekdayHasSchedules,
  generateWeekColumns,
  getDayKey,
  getFirstDay,
  getLastDay,
  getSidebarTab,
} from './helpers';
import { useGetWorkloadCalendarSidebar } from '@services/workload/calendar/sidebar';
import { useGetWorkloadCalendar } from '@services/workload/calendar';
import { differenceInCalendarDays, getDay } from 'date-fns';
import { useGetWorkloadMonthlyCalendar } from '@services/workload/calendar/monthly';
import {
  computeMonthlyDrafts,
  filterWorkloadMonthlyRecords,
} from '@services/workload/calendar/hiddenRecords';
import { useQueryParams } from '@hooks/queryParams';
import { useWorkloadFilterParams } from '@hooks/workload/filterParams';
import { useAppContext } from '@contexts/app';
import { toZonedTime } from 'date-fns-tz';
import { useInView } from 'react-intersection-observer';
import {
  getWorkloadDisplayOptionsSettingsEndpoint,
  getDisplayOptionsSettings,
} from '@components/workload/drawer/displayOptions/helpers';

// prefetch the next page before the sentinel is actually scrolled into view
const JOBS_SIDEBAR_PREFETCH_MARGIN = '0px 0px 200px 0px';

const WorkloadContext = createContext<WorkloadContextData | Record<string, never>>({});

export const WorkloadProvider = ({ children }: WorkloadProviderProps) => {
  const { tenant, displayOptions } = useAppContext();
  const [state, setState] = useState<WorkloadData>(initialState);
  const { visibleDays, date, expandedWeekendDays, expandedWeekendWeekdays } = state;

  const { params } = useQueryParams();
  const { filterParams, filterKey } = useWorkloadFilterParams();
  const { type, searchSidebar, divisionIds, team, workflowIds } = params;

  const isTwoWeekly = type === 'two-week';
  const { showHidden } = state;

  const [jobsPage, setJobsPage] = useState(1);
  const [jobsRecords, setJobsRecords] = useState<unknown[]>([]);

  const settings = useMemo(
    () =>
      getDisplayOptionsSettings(
        displayOptions || [],
        getWorkloadDisplayOptionsSettingsEndpoint(type),
      ),
    [displayOptions, type],
  );

  const weekSettings = useMemo(
    () => getDisplayOptionsSettings(displayOptions || [], 'weekCalendar'),
    [displayOptions],
  );

  const baseWeekColumns = useMemo(
    () =>
      visibleDays ? generateWeekColumns(visibleDays, date, weekSettings, expandedWeekendDays) : [],
    [visibleDays, date, weekSettings, expandedWeekendDays],
  );

  const weekDates = useMemo(
    () => baseWeekColumns.filter((column) => !column.collapsed).map((column) => column.date),
    [baseWeekColumns],
  );

  const first = useMemo(() => getFirstDay(date, type), [date, type]);
  const last = useMemo(() => getLastDay(date, type), [date, type]);
  const difference = differenceInCalendarDays(last, first);

  const calendar = useGetWorkloadCalendar(date, weekDates);
  const monthly = useGetWorkloadMonthlyCalendar(first);
  const jobs = useGetWorkloadCalendarSidebar('jobs', jobsPage);
  const specialties = useGetWorkloadCalendarSidebar('specialties');

  const jobsPagy = jobs.data?.pagy;
  const jobsFailed = Boolean(jobs.error) && !jobs.data;
  const specialtiesFailed = Boolean(specialties.error) && !specialties.data;
  const isJobsLastPage = Boolean(jobsPagy) && jobsPagy?.next == null;
  const jobsFilterKey = JSON.stringify({
    searchSidebar,
    divisionIds,
    team,
    workflowIds,
  });

  const { ref: jobsLoaderRef, inView: jobsInView } = useInView({
    skip: jobs.isLoading,
    rootMargin: JOBS_SIDEBAR_PREFETCH_MARGIN,
  });

  const setData = useCallback((prop: keyof WorkloadData, value: unknown): void => {
    setState((prev) => ({ ...prev, [prop]: value }));
  }, []);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      sidebarTab: getSidebarTab(settings),
    }));
  }, [settings]);

  useEffect(() => {
    if (!tenant.zone) return;
    setData('date', toZonedTime(new Date(), tenant.zone));
  }, [tenant.zone, setData]);

  useEffect(() => {
    setData('dates', weekDates);
  }, [weekDates, setData]);

  useEffect(() => {
    const daysInView = new Set(baseWeekColumns.map((column) => getDayKey(column.date)));
    setState((prev) => {
      const expanded = prev.expandedWeekendDays.filter((day) => daysInView.has(day));
      if (expanded.length === prev.expandedWeekendDays.length) return prev;
      return { ...prev, expandedWeekendDays: expanded };
    });
  }, [baseWeekColumns]);

  useEffect(() => {
    setState((prev) =>
      prev.expandedWeekendDays.length ? { ...prev, expandedWeekendDays: [] } : prev,
    );
  }, [type]);

  useEffect(() => {
    setState((prev) =>
      prev.expandedWeekendWeekdays.length ? { ...prev, expandedWeekendWeekdays: [] } : prev,
    );
  }, [date, type]);

  useEffect(() => {
    setJobsPage(1);
    setJobsRecords([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsFilterKey]);

  useEffect(() => {
    setJobsRecords((prev) =>
      jobsPage === 1 ? jobs.data?.records || [] : [...prev, ...(jobs.data?.records || [])],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.data?.records, jobsPage]);

  useEffect(() => {
    if (!jobsInView || !jobsPagy?.next) return;
    const timeout = setTimeout(() => setJobsPage(jobsPagy.next as number), 400);
    return () => clearTimeout(timeout);
  }, [jobsInView, jobsPagy?.next]);

  const { visibleRecords, hiddenCount, hiddenDaysCount } = useMemo(
    () => filterWorkloadMonthlyRecords(monthly.data?.records || {}, filterParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthly.data?.records, filterKey],
  );

  const monthlyDrafts = useMemo(() => computeMonthlyDrafts(visibleRecords), [visibleRecords]);

  const monthlyDays = useMemo(() => generateDays(difference + 1, first), [difference, first]);

  const monthlyCountsByDay = useMemo(() => {
    const records = showHidden ? monthly.data?.records || {} : visibleRecords;
    return Object.fromEntries(
      Object.entries(records).map(([day, schedules]) => [day, schedules.length]),
    );
  }, [showHidden, monthly.data?.records, visibleRecords]);

  useEffect(() => {
    if (monthly.isLoading) return;
    setState((prev) => {
      const expanded = prev.expandedWeekendWeekdays.filter((weekday) =>
        weekdayHasSchedules(monthlyDays, weekday, monthlyCountsByDay),
      );
      if (expanded.length === prev.expandedWeekendWeekdays.length) return prev;
      return { ...prev, expandedWeekendWeekdays: expanded };
    });
  }, [monthlyDays, monthlyCountsByDay, monthly.isLoading]);

  const monthlyLayout = useMemo(() => {
    const columns = generateMonthlyColumns(
      monthlyDays,
      settings,
      expandedWeekendWeekdays,
      monthlyCountsByDay,
    );
    const weekdays = new Set(columns.map((column) => column.weekday));

    return {
      columns,
      weekLength: columns.length,
      dates: monthlyDays.filter((day) => weekdays.has(getDay(day))),
    };
  }, [monthlyDays, settings, expandedWeekendWeekdays, monthlyCountsByDay]);

  const weekCountsByDay = useMemo(() => {
    const foremen = showHidden ? calendar.data?.records || [] : calendar.allDatesVisibleRecords;
    const withoutTeam = showHidden
      ? calendar.data?.withoutTeam || []
      : calendar.allDatesVisibleWithoutTeam;
    return countSchedulesByDay([
      ...foremen.flatMap((foreman) => foreman.schedules),
      ...withoutTeam,
    ]);
  }, [
    showHidden,
    calendar.data?.records,
    calendar.data?.withoutTeam,
    calendar.allDatesVisibleRecords,
    calendar.allDatesVisibleWithoutTeam,
  ]);

  useEffect(() => {
    if (calendar.isLoading) return;
    setState((prev) => {
      const expanded = prev.expandedWeekendDays.filter((day) => weekCountsByDay[day]);
      if (expanded.length === prev.expandedWeekendDays.length) return prev;
      return { ...prev, expandedWeekendDays: expanded };
    });
  }, [weekCountsByDay, calendar.isLoading]);

  const weekColumns = useMemo(
    () =>
      baseWeekColumns.flatMap((column) => {
        if (!column.collapsed) return [column];
        const hiddenCount = weekCountsByDay[getDayKey(column.date)] || 0;
        return hiddenCount ? [{ ...column, hiddenCount }] : [];
      }),
    [baseWeekColumns, weekCountsByDay],
  );

  const contextValue = useMemo<WorkloadContextData>(
    () => ({
      ...state,
      setState,
      sidebar: {
        jobs: {
          isLoading: jobs.isLoading,
          error: jobsFailed,
          retry: () => void jobs.refetch(),
          records: jobsRecords,
          pagy: jobsPagy,
          loader: {
            ref: !isJobsLastPage ? jobsLoaderRef : undefined,
            visible: !jobsFailed && ((jobs.isLoading && jobsPage !== 1) || !isJobsLastPage),
          },
        },
        specialties: {
          isLoading: specialties.isLoading,
          error: specialtiesFailed,
          retry: () => void specialties.refetch(),
          records: specialties.data?.records || [],
        },
      },
      setData,
      isTwoWeekly,
      weekColumns,
      dates: weekDates,
      calendar: {
        isLoading: calendar.isLoading,
        records: showHidden ? calendar.data?.records || [] : calendar.visibleRecords,
        withoutTeam: showHidden ? calendar.data?.withoutTeam || [] : calendar.visibleWithoutTeam,
        drafts: calendar.drafts,
        hiddenCount: calendar.hiddenCount,
        hiddenForemenCount: calendar.hiddenForemenCount,
      },
      monthly: {
        ...state.monthly,
        ...monthlyLayout,
        records: showHidden ? monthly.data?.records || {} : visibleRecords,
        drafts: monthlyDrafts,
        isLoading: monthly.isLoading,
        limit: isTwoWeekly ? TWO_WEEKLY_LIMIT : MONTHLY_LIMIT,
        first,
        last,
        hiddenCount,
        hiddenDaysCount,
      },
    }),
    [
      state,
      setState,
      jobs.isLoading,
      jobs.refetch,
      jobsFailed,
      jobsRecords,
      jobsPagy,
      jobsPage,
      isJobsLastPage,
      jobsLoaderRef,
      specialties.isLoading,
      specialties.refetch,
      specialtiesFailed,
      specialties.data?.records,
      setData,
      isTwoWeekly,
      showHidden,
      weekColumns,
      weekDates,
      monthlyLayout,
      calendar.isLoading,
      calendar.data?.records,
      calendar.data?.withoutTeam,
      calendar.visibleRecords,
      calendar.visibleWithoutTeam,
      calendar.drafts,
      calendar.hiddenCount,
      calendar.hiddenForemenCount,
      monthly.data?.records,
      monthly.isLoading,
      visibleRecords,
      monthlyDrafts,
      first,
      last,
      hiddenCount,
      hiddenDaysCount,
    ],
  );

  return <WorkloadContext.Provider value={contextValue}>{children}</WorkloadContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkload = () => useContext(WorkloadContext);
